import "@/index.css";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/auth/LoginPage";
import SignupPage from "@/pages/auth/SignupPage";
import { Suspense, lazy, useEffect } from "react";
import { Loader2 } from "lucide-react";

// The coach app, client app and public pages load only when someone opens them,
// so the website itself downloads a much smaller file.
const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));
const AppLayout = lazy(() => import("@/components/app/AppLayout"));
const RequireAuth = named(() => import("@/components/app/bits"), "RequireAuth");
const DashboardPage = lazy(() => import("@/pages/app/DashboardPage"));
const ClientsPage = lazy(() => import("@/pages/app/ClientsPage"));
const ClientProfilePage = lazy(() => import("@/pages/app/ClientProfilePage"));
const SettingsPage = lazy(() => import("@/pages/app/SettingsPage"));
const WorkoutsPage = lazy(() => import("@/pages/app/WorkoutsPage"));
const ProgramEditorPage = lazy(() => import("@/pages/app/ProgramEditorPage"));
const NutritionListPage = named(() => import("@/pages/app/NutritionPages"), "NutritionListPage");
const MealPlanEditorPage = named(() => import("@/pages/app/NutritionPages"), "MealPlanEditorPage");
const CheckinsPage = named(() => import("@/pages/app/CommsPages"), "CheckinsPage");
const MessagesPage = named(() => import("@/pages/app/CommsPages"), "MessagesPage");
const RemindersPage = named(() => import("@/pages/app/CommsPages"), "RemindersPage");
const CheckinFormEditorPage = named(() => import("@/pages/app/CheckinForms"), "CheckinFormEditorPage");
const MembershipsPage = named(() => import("@/pages/app/Memberships"), "MembershipsPage");
const AutopilotPage = named(() => import("@/pages/app/Autopilot"), "AutopilotPage");
const EmailTemplatesPage = lazy(() => import("@/pages/app/EmailTemplates"));
const JoinMembershipPage = named(() => import("@/pages/public/MembershipPages"), "JoinMembershipPage");
const PricingPage = named(() => import("@/pages/public/MembershipPages"), "PricingPage");
const ProposalsPage = named(() => import("@/pages/app/ProposalPages"), "ProposalsPage");
const ProposalEditorPage = named(() => import("@/pages/app/ProposalPages"), "ProposalEditorPage");
const AgentPage = named(() => import("@/pages/app/BusinessPages"), "AgentPage");
const PaymentsPage = named(() => import("@/pages/app/BusinessPages"), "PaymentsPage");
const JoinPage = named(() => import("@/pages/public/PublicPages"), "JoinPage");
const ProposalPublicPage = named(() => import("@/pages/public/PublicPages"), "ProposalPublicPage");
const ClientApp = lazy(() => import("@/pages/portal/ClientApp"));

function PageLoading() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-white text-neutral-400" role="status" aria-label="Loading">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

/** Every new page opens at the top (links to #sections still jump to their spot). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

/** Where the phone app opens: your training (client), your dashboard (coach), or the login page. */
function OpenApp() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (!user) return <Navigate to="/client" replace />;
  return <Navigate to={user.role === "client" ? "/me" : "/app"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/client" element={<LoginPage audience="client" />} />
          <Route path="/open" element={<OpenApp />} />
          <Route path="/m/:token" element={<JoinMembershipPage />} />
          <Route path="/c/:token" element={<PricingPage />} />
          <Route path="/client-login" element={<Navigate to="/client" replace />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/p/:token" element={<ProposalPublicPage />} />
          <Route path="/join/:token" element={<JoinPage />} />
          <Route path="/me/*" element={<ClientApp />} />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:id" element={<ClientProfilePage />} />
            <Route path="check-ins" element={<CheckinsPage />} />
            <Route path="check-ins/forms/:id" element={<CheckinFormEditorPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="messages/:clientId" element={<MessagesPage />} />
            <Route path="reminders" element={<RemindersPage />} />
            <Route path="tasks" element={<Navigate to="/app/reminders" replace />} />
            <Route path="workouts" element={<WorkoutsPage />} />
            <Route path="workouts/programs/:id" element={<ProgramEditorPage />} />
            <Route path="nutrition" element={<NutritionListPage />} />
            <Route path="nutrition/:id" element={<MealPlanEditorPage />} />
            <Route path="proposals" element={<ProposalsPage />} />
            <Route path="proposals/:id" element={<ProposalEditorPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="memberships" element={<MembershipsPage />} />
            <Route path="autopilot" element={<AutopilotPage />} />
            <Route path="emails" element={<EmailTemplatesPage />} />
            <Route path="agent" element={<AgentPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <Toaster position="top-center" richColors duration={2500} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
